import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { assessmentService } from '../api/services';
import useApi from '../hooks/useApi';
import Navbar from '../components/layout/Navbar';
import {
  ChevronRight, ChevronLeft, CheckCircle2, AlertCircle,
  Loader2, Star, Target, TrendingUp, Award, Download,
  Clock, FileText, Brain, ArrowRight,
} from 'lucide-react';
import toast from 'react-hot-toast';

const OUTCOME_STYLES = {
  IDEA_STAGE_INNOVATOR:     { color: 'text-blue-700',   bg: 'bg-blue-50',   border: 'border-blue-200',   label: 'Idea Stage Innovator' },
  EARLY_STAGE_ENTREPRENEUR: { color: 'text-amber-700',  bg: 'bg-amber-50',  border: 'border-amber-200',  label: 'Early-Stage Entrepreneur' },
  PROTOTYPE_MVP_BUILDER:    { color: 'text-orange-700', bg: 'bg-orange-50', border: 'border-orange-200', label: 'Prototype/MVP Builder' },
  GROWTH_COMMERCIALIZATION: { color: 'text-forest-700', bg: 'bg-forest-50', border: 'border-forest-200', label: 'Growth & Commercialization Candidate' },
  ADVANCED_ENTERPRISE:      { color: 'text-purple-700', bg: 'bg-purple-50', border: 'border-purple-200', label: 'Advanced Enterprise Support Candidate' },
};

// ── PDF Download helper ──────────────────────────────────────
const downloadResultsPDF = (submission) => {
  const parseJson = (val, fallback) => {
    if (!val) return fallback;
    if (typeof val === 'object') return val;
    try { return JSON.parse(val); } catch { return fallback; }
  };

  const areaScores = parseJson(submission.areaScores, {});
  const strengths  = parseJson(submission.strengths, []);
  const gapAreas   = parseJson(submission.gapAreas, []);
  const style      = OUTCOME_STYLES[submission.outcome] || OUTCOME_STYLES.IDEA_STAGE_INNOVATOR;
  const completedDate = submission.completedAt
    ? new Date(submission.completedAt).toLocaleDateString('en-GH', { dateStyle: 'long' })
    : 'N/A';

  const areaRows = Object.values(areaScores)
    .sort((a, b) => (a.order || 0) - (b.order || 0))
    .map(a => `
      <tr>
        <td style="padding:8px 12px;border-bottom:1px solid #f0ede7;font-size:12px;color:#1a3a1a;">${a.name}</td>
        <td style="padding:8px 12px;border-bottom:1px solid #f0ede7;text-align:center;">
          <div style="background:#f0f0f0;border-radius:20px;height:8px;width:100px;display:inline-block;vertical-align:middle;margin-right:8px;">
            <div style="background:${a.percentage >= 70 ? '#16a34a' : a.percentage >= 50 ? '#d97706' : '#dc2626'};width:${a.percentage}%;height:100%;border-radius:20px;"></div>
          </div>
          <span style="font-size:12px;font-weight:600;color:${a.percentage >= 70 ? '#16a34a' : a.percentage >= 50 ? '#d97706' : '#dc2626'};">${a.percentage}%</span>
        </td>
      </tr>`)
    .join('');

  const html = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8"/>
      <title>TourEnt Hub — Assessment Results</title>
      <style>
        * { margin:0; padding:0; box-sizing:border-box; }
        body { font-family: 'Segoe UI', sans-serif; background:#fff; color:#1a3a1a; padding:40px; }
        .header { border-bottom:3px solid #0F6E56; padding-bottom:20px; margin-bottom:28px; display:flex; justify-content:space-between; align-items:flex-end; }
        .logo { font-size:22px; font-weight:700; color:#0F6E56; }
        .logo span { color:#BA7517; }
        .logo-sub { font-size:10px; letter-spacing:.1em; text-transform:uppercase; color:#888; margin-top:3px; }
        .date { font-size:11px; color:#888; text-align:right; }
        .outcome-box { background:#f4faf0; border:2px solid #3B6D11; border-radius:12px; padding:24px; text-align:center; margin-bottom:24px; }
        .outcome-label { font-size:11px; font-weight:600; text-transform:uppercase; letter-spacing:.1em; color:#666; margin-bottom:8px; }
        .outcome-title { font-size:22px; font-weight:700; color:#085041; margin-bottom:12px; }
        .score-big { font-size:48px; font-weight:700; color:#0F6E56; line-height:1; }
        .score-sub { font-size:13px; color:#666; margin-top:4px; }
        .section-title { font-size:14px; font-weight:600; color:#0F6E56; text-transform:uppercase; letter-spacing:.08em; margin:24px 0 12px; border-left:3px solid #0F6E56; padding-left:10px; }
        table { width:100%; border-collapse:collapse; }
        th { background:#0F6E56; color:#fff; padding:10px 12px; text-align:left; font-size:11px; text-transform:uppercase; letter-spacing:.06em; }
        .tag { display:inline-block; background:#f0f8f0; border:1px solid #3B6D11; color:#085041; border-radius:20px; padding:4px 12px; font-size:11px; margin:3px; }
        .tag.gap { background:#fff8f0; border-color:#d97706; color:#92400e; }
        .footer { margin-top:40px; padding-top:16px; border-top:1px solid #e0e0e0; font-size:10px; color:#aaa; text-align:center; }
        .recommended { background:#f0f8f0; border:1px solid #3B6D11; border-radius:8px; padding:14px 16px; font-size:12px; color:#085041; margin-top:16px; }
      </style>
    </head>
    <body>
      <div class="header">
        <div>
          <div class="logo">TourEnt <span>Hub</span></div>
          <div class="logo-sub">Tourism Entrepreneurship Hub</div>
        </div>
        <div class="date">
          <div style="font-weight:600;font-size:13px;">Skills & Knowledge Gap Assessment</div>
          <div>Completed: ${completedDate}</div>
        </div>
      </div>

      <div class="outcome-box">
        <div class="outcome-label">Assessment Outcome</div>
        <div class="outcome-title">${style.label}</div>
        <div class="score-big">${submission.totalScore ?? '--'}</div>
        <div class="score-sub">Overall Score out of 100</div>
      </div>

      <div class="section-title">Performance by Area</div>
      <table>
        <thead><tr><th>Assessment Area</th><th style="text-align:center;">Score</th></tr></thead>
        <tbody>${areaRows}</tbody>
      </table>

      ${strengths.length > 0 ? `
      <div class="section-title">Your Strengths</div>
      <div>${strengths.map(s => `<span class="tag">${s}</span>`).join('')}</div>` : ''}

      ${gapAreas.length > 0 ? `
      <div class="section-title">Areas to Develop</div>
      <div>${gapAreas.map(g => `<span class="tag gap">${g}</span>`).join('')}</div>` : ''}

      ${submission.recommendedLevel ? `
      <div class="recommended">
        📍 <strong>Recommended Starting Level:</strong> Level ${submission.recommendedLevel} of the Tourent Cycle.
        Your mentorship will be tailored to address your development areas and build on your existing strengths.
      </div>` : ''}

      <div class="footer">
        TourEnt Hub · Tourism Entrepreneurship Hub · tourenthub.com<br/>
        This report was generated automatically based on your assessment responses.
      </div>
    </body>
    </html>`;

  const win = window.open('', '_blank');
  win.document.write(html);
  win.document.close();
  win.focus();
  setTimeout(() => { win.print(); }, 500);
};

// ── Question Component ────────────────────────────────────────
const QuestionCard = ({ question, answer, onChange }) => {
  if (question.type === 'MULTIPLE_CHOICE') {
    return (
      <div className="space-y-3">
        {(question.options || []).map((opt, i) => (
          <button key={i} onClick={() => onChange({ selectedOption: i })}
            className={`w-full text-left px-4 py-3.5 rounded-xl border-2 transition-all text-sm ${
              answer?.selectedOption === i
                ? 'border-forest-600 bg-forest-50 text-forest-900 font-medium'
                : 'border-cream-200 bg-white text-forest-700 hover:border-forest-300 hover:bg-cream-50'
            }`}>
            <span className={`inline-flex w-6 h-6 rounded-full border-2 items-center justify-center mr-3 text-xs font-bold flex-shrink-0 ${
              answer?.selectedOption === i ? 'border-forest-600 bg-forest-600 text-white' : 'border-cream-300'
            }`}>
              {String.fromCharCode(65 + i)}
            </span>
            {opt}
          </button>
        ))}
      </div>
    );
  }

  if (question.type === 'SCALE') {
    const val = answer?.scaleValue ?? null;
    const labels = ['', 'Not at all', 'Slightly', 'Moderately', 'Quite well', 'Very well'];
    return (
      <div className="space-y-4">
        <div className="flex gap-3 justify-between">
          {[1, 2, 3, 4, 5].map(n => (
            <button key={n} onClick={() => onChange({ scaleValue: n })}
              className={`flex-1 flex flex-col items-center gap-2 py-4 rounded-xl border-2 transition-all ${
                val === n
                  ? 'border-forest-600 bg-forest-50'
                  : 'border-cream-200 bg-white hover:border-forest-300'
              }`}>
              <span className={`font-serif text-2xl font-bold ${val === n ? 'text-forest-700' : 'text-forest-400'}`}>{n}</span>
              <Star size={14} className={val === n ? 'text-gold-400 fill-gold-400' : 'text-cream-300'} />
            </button>
          ))}
        </div>
        <div className="flex justify-between text-xs text-forest-400 px-1">
          <span>{labels[1]}</span>
          <span>{labels[5]}</span>
        </div>
        {val && <p className="text-sm text-center text-forest-600 font-medium">{labels[val]}</p>}
      </div>
    );
  }

  if (question.type === 'YES_NO') {
    return (
      <div className="grid grid-cols-2 gap-4">
        {[{ label: 'Yes', value: true }, { label: 'No', value: false }].map(opt => (
          <button key={String(opt.value)} onClick={() => onChange({ boolValue: opt.value })}
            className={`py-5 rounded-xl border-2 text-sm font-semibold transition-all ${
              answer?.boolValue === opt.value
                ? opt.value
                  ? 'border-forest-600 bg-forest-600 text-white'
                  : 'border-rust-400 bg-rust-400 text-white'
                : 'border-cream-200 bg-white text-forest-700 hover:border-forest-300'
            }`}>
            {opt.label}
          </button>
        ))}
      </div>
    );
  }

  return null;
};

// ── Results Screen ────────────────────────────────────────────
const ResultsScreen = ({ result, onContinue }) => {
  const style = OUTCOME_STYLES[result.outcome] || OUTCOME_STYLES.IDEA_STAGE_INNOVATOR;

  return (
    <div className="min-h-screen bg-cream-50 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-2xl space-y-6">

        {/* Header */}
        <div className="text-center">
          <div className="w-20 h-20 bg-forest-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Award size={36} className="text-forest-600" />
          </div>
          <h1 className="font-serif text-3xl font-bold text-forest-900 mb-2">Assessment Complete!</h1>
          <p className="text-forest-500 text-sm">Here's your personalised skills profile</p>
        </div>

        {/* Outcome badge */}
        <div className={`border-2 rounded-2xl p-6 text-center ${style.bg} ${style.border}`}>
          <p className="text-xs font-semibold uppercase tracking-widest text-forest-500 mb-2">Your Category</p>
          <h2 className={`font-serif text-2xl font-bold ${style.color} mb-2`}>{style.label}</h2>
          <div className="flex items-center justify-center gap-2 mt-3">
            <div className="font-serif text-4xl font-bold text-forest-900">{result.totalScore}</div>
            <div className="text-forest-400 text-lg">/100</div>
          </div>
          <p className="text-xs text-forest-500 mt-1">Overall Score</p>
        </div>

        {/* Area scores */}
        <div className="bg-white border border-cream-200 rounded-2xl p-6">
          <h3 className="font-serif font-semibold text-forest-900 mb-4">Your Performance by Area</h3>
          <div className="space-y-3">
            {Object.entries(result.areaScores || {})
              .sort((a, b) => (a[1].order || 0) - (b[1].order || 0))
              .map(([, area]) => (
                <div key={area.name}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium text-forest-700 truncate flex-1 mr-3">{area.name}</span>
                    <span className={`text-xs font-bold flex-shrink-0 ${
                      area.percentage >= 70 ? 'text-green-600'
                      : area.percentage >= 50 ? 'text-amber-600'
                      : 'text-rust-500'
                    }`}>{area.percentage}%</span>
                  </div>
                  <div className="h-2 bg-cream-100 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full transition-all duration-700 ${
                      area.percentage >= 70 ? 'bg-green-500'
                      : area.percentage >= 50 ? 'bg-amber-400'
                      : 'bg-rust-400'
                    }`} style={{ width: `${area.percentage}%` }} />
                  </div>
                </div>
              ))}
          </div>
        </div>

        {/* Strengths + Gaps */}
        <div className="grid sm:grid-cols-2 gap-4">
          {result.strengths?.length > 0 && (
            <div className="bg-green-50 border border-green-200 rounded-2xl p-5">
              <h4 className="font-sans font-semibold text-green-800 text-sm mb-3 flex items-center gap-2">
                <CheckCircle2 size={15} /> Your Strengths
              </h4>
              <ul className="space-y-1.5">
                {result.strengths.map((s, i) => (
                  <li key={i} className="text-xs text-green-700 flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-500 flex-shrink-0 mt-1.5" />
                    {s}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {result.gapAreas?.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5">
              <h4 className="font-sans font-semibold text-amber-800 text-sm mb-3 flex items-center gap-2">
                <Target size={15} /> Areas to Develop
              </h4>
              <ul className="space-y-1.5">
                {result.gapAreas.map((g, i) => (
                  <li key={i} className="text-xs text-amber-700 flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500 flex-shrink-0 mt-1.5" />
                    {g}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="bg-forest-50 border border-forest-200 rounded-2xl p-4 text-xs text-forest-700 flex items-start gap-2.5">
          <TrendingUp size={14} className="text-forest-500 flex-shrink-0 mt-0.5" />
          <span>
            Based on your results, you'll start at <strong>Level {result.recommendedLevel}</strong> of the Tourent Cycle.
            Your mentorship will be tailored to address your development areas and build on your existing strengths.
          </span>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <button onClick={() => downloadResultsPDF(result)}
            className="flex-1 flex items-center justify-center gap-2 py-3 rounded-full border-2 border-forest-600 text-forest-700 font-semibold text-sm hover:bg-forest-50 transition-colors">
            <Download size={15} /> Download PDF Results
          </button>
          <button onClick={onContinue} className="flex-1 btn-primary justify-center text-base py-3">
            Continue to Dashboard →
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Start Prompt Screen ──────────────────────────────────────
const StartPromptScreen = ({ totalQuestions, totalAreas, onStart }) => (
  <div className="min-h-screen bg-cream-50 flex items-center justify-center px-4 py-12">
    <div className="w-full max-w-xl space-y-6">

      {/* Header */}
      <div className="text-center">
        <div className="w-20 h-20 bg-forest-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Brain size={36} className="text-forest-600" />
        </div>
        <h1 className="font-serif text-3xl font-bold text-forest-900 mb-2">
          Skills & Knowledge Gap Assessment
        </h1>
        <p className="text-forest-500 text-sm leading-relaxed max-w-md mx-auto">
          Before you begin your journey on TourEnt Hub, we need to understand
          your current skills and knowledge so we can tailor your mentorship pathway.
        </p>
      </div>

      {/* Info cards */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white border border-cream-200 rounded-2xl p-4 text-center">
          <div className="font-serif text-2xl font-bold text-forest-900">{totalAreas}</div>
          <div className="text-xs text-forest-500 mt-1">Assessment Areas</div>
        </div>
        <div className="bg-white border border-cream-200 rounded-2xl p-4 text-center">
          <div className="font-serif text-2xl font-bold text-forest-900">{totalQuestions}</div>
          <div className="text-xs text-forest-500 mt-1">Questions</div>
        </div>
        <div className="bg-white border border-cream-200 rounded-2xl p-4 text-center">
          <Clock size={22} className="text-forest-600 mx-auto mb-1" />
          <div className="text-xs text-forest-500">~10 mins</div>
        </div>
      </div>

      {/* What to expect */}
      <div className="bg-white border border-cream-200 rounded-2xl p-6 space-y-4">
        <h3 className="font-serif font-semibold text-forest-900 text-base">What to expect</h3>
        <div className="space-y-3">
          {[
            { icon: FileText, text: 'Questions across 10 core entrepreneurship and tourism areas' },
            { icon: Target,   text: 'Multiple choice, scale rating and yes/no questions' },
            { icon: Award,    text: 'Receive a personalised skills profile and outcome category' },
            { icon: TrendingUp, text: 'Your results guide your mentorship pathway from startup to scale' },
          ].map((item, i) => (
            <div key={i} className="flex items-start gap-3">
              <div className="w-7 h-7 bg-forest-50 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                <item.icon size={14} className="text-forest-600" />
              </div>
              <p className="text-sm text-forest-600 leading-relaxed">{item.text}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Important note */}
      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-start gap-3">
        <AlertCircle size={16} className="text-amber-500 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-amber-700 leading-relaxed">
          <strong>Important:</strong> This assessment can only be completed once.
          Take your time and answer honestly — there are no right or wrong answers.
          Your results will be used to personalise your mentorship experience.
        </p>
      </div>

      <button onClick={onStart}
        className="btn-primary w-full justify-center text-base py-4 flex items-center gap-2">
        Start Assessment <ArrowRight size={16} />
      </button>

    </div>
  </div>
);

// ── Already Completed Screen ─────────────────────────────────
const AlreadyCompletedScreen = ({ submission, onViewResults, onGoToDashboard }) => {
  const style = OUTCOME_STYLES[submission.outcome] || OUTCOME_STYLES.IDEA_STAGE_INNOVATOR;
  return (
    <div className="min-h-screen bg-cream-50 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-lg space-y-6 text-center">
        <div className="w-20 h-20 bg-forest-100 rounded-full flex items-center justify-center mx-auto">
          <CheckCircle2 size={36} className="text-forest-600" />
        </div>
        <div>
          <h1 className="font-serif text-3xl font-bold text-forest-900 mb-2">Assessment Already Completed</h1>
          <p className="text-forest-500 text-sm">You have already completed the skills assessment.</p>
        </div>
        <div className={`border-2 rounded-2xl p-6 ${style.bg} ${style.border}`}>
          <p className="text-xs font-semibold uppercase tracking-widest text-forest-500 mb-2">Your Category</p>
          <h2 className={`font-serif text-2xl font-bold ${style.color} mb-3`}>{style.label}</h2>
          <div className="flex items-center justify-center gap-2">
            <div className="font-serif text-4xl font-bold text-forest-900">{submission.totalScore}</div>
            <div className="text-forest-400 text-lg">/100</div>
          </div>
          <p className="text-xs text-forest-500 mt-1">Overall Score</p>
          {submission.completedAt && (
            <p className="text-xs text-forest-400 mt-2">
              Completed on {new Date(submission.completedAt).toLocaleDateString('en-GH', { dateStyle: 'medium' })}
            </p>
          )}
        </div>
        <div className="flex flex-col sm:flex-row gap-3 justify-center flex-wrap">
          <button onClick={onViewResults} className="btn-primary flex items-center justify-center gap-2">
            <Award size={15} /> View Full Results
          </button>
          <button onClick={() => downloadResultsPDF(submission)}
            className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-full border-2 border-forest-600 text-forest-700 font-semibold text-sm hover:bg-forest-50 transition-colors">
            <Download size={15} /> Download PDF
          </button>
          <button onClick={onGoToDashboard}
            className="px-5 py-2.5 rounded-full border border-cream-300 text-sm font-medium text-forest-600 hover:border-forest-400 transition-colors">
            Go to Dashboard →
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Main Assessment Page ──────────────────────────────────────
const AssessmentPage = () => {
  const navigate  = useNavigate();
  const [currentArea,  setCurrentArea]  = useState(0);
  const [currentQ,     setCurrentQ]     = useState(0);
  const [answers,      setAnswers]       = useState({});  // { questionId: { selectedOption?, scaleValue?, boolValue? } }
  const [submitting,   setSubmitting]    = useState(false);
  const [result,       setResult]        = useState(null);
  // Track existing submission — if already completed, don't show questions again
  const [existingSubmission, setExistingSubmission] = useState(null);
  const [revisionNotice,     setRevisionNotice]      = useState(null);
  const [checkingSubmission, setCheckingSubmission] = useState(true);
  const [showFullResults,    setShowFullResults]    = useState(false);
  // Start prompt — shown before first question
  const [started, setStarted] = useState(false);

  const { data, loading, error } = useApi(() => assessmentService.getAssessment());
  const areas = data?.data?.areas ?? data?.areas ?? [];

  // On mount — check if client has already completed the assessment
  React.useEffect(() => {
    assessmentService.getMySubmission()
      .then(res => {
        const sub = res.data?.data ?? res.data;
        // Only block re-entry if status is COMPLETED or REVIEWED
        if (sub?.status === 'COMPLETED' || sub?.status === 'REVIEWED') {
          setExistingSubmission(sub?.submission ?? sub);
        }
        if (sub?.status === 'REVISION_REQUESTED') {
          setRevisionNotice(sub?.submission?.adminNotes ?? sub?.adminNotes ?? null);
        }
      })
      .catch(() => {
        // If API fails or no submission — allow taking the assessment
      })
      .finally(() => setCheckingSubmission(false));
  }, []);

  const area      = areas[currentArea];
  const questions = area?.questions ?? [];
  const question  = questions[currentQ];
  const answer    = question ? answers[question.id] : null;

  const totalQuestions  = areas.reduce((s, a) => s + (a.questions?.length ?? 0), 0);
  const answeredCount   = Object.keys(answers).length;
  const progressPct     = totalQuestions > 0 ? Math.round((answeredCount / totalQuestions) * 100) : 0;

  // Questions answered in current area
  const areaAnswered = questions.filter(q => answers[q.id] !== undefined).length;
  const isLastArea   = currentArea === areas.length - 1;
  const isLastQ      = currentQ    === questions.length - 1;

  const handleAnswer = (val) => {
    if (!question) return;
    setAnswers(prev => ({ ...prev, [question.id]: val }));
  };

  const handleNext = () => {
    if (!answer) { toast.error('Please answer this question before continuing'); return; }
    if (!isLastQ) { setCurrentQ(q => q + 1); return; }
    if (!isLastArea) { setCurrentArea(a => a + 1); setCurrentQ(0); return; }
    // All done — submit
    handleSubmit();
  };

  const handlePrev = () => {
    if (currentQ > 0) { setCurrentQ(q => q - 1); return; }
    if (currentArea > 0) {
      setCurrentArea(a => a - 1);
      setCurrentQ(areas[currentArea - 1]?.questions?.length - 1 || 0);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const payload = Object.entries(answers).map(([questionId, val]) => ({
        questionId,
        selectedOption: val.selectedOption ?? null,
        scaleValue:     val.scaleValue     ?? null,
        boolValue:      val.boolValue      ?? null,
      }));
      const res = await assessmentService.submit({ answers: payload });
      const data = res.data?.data ?? res.data;
      setResult(data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    return <ResultsScreen result={result} onContinue={() => navigate('/dashboard')} />;
  }

  // Still checking whether assessment was already completed
  if (checkingSubmission) return (
    <div className="min-h-screen bg-cream-50 flex items-center justify-center">
      <div className="text-center space-y-3">
        <Loader2 size={32} className="animate-spin text-forest-600 mx-auto" />
        <p className="text-forest-500 text-sm">Loading assessment...</p>
      </div>
    </div>
  );

  // Assessment already completed — show summary or full results
  if (existingSubmission && !showFullResults) {
    return (
      <AlreadyCompletedScreen
        submission={existingSubmission}
        onViewResults={() => setShowFullResults(true)}
        onGoToDashboard={() => navigate('/dashboard')}
      />
    );
  }

  // Show full results (from "View Full Results" button on already-completed screen)
  if (existingSubmission && showFullResults) {
    const parsed = {
      ...existingSubmission,
      areaScores: typeof existingSubmission.areaScores === 'string'
        ? JSON.parse(existingSubmission.areaScores)
        : existingSubmission.areaScores ?? {},
      strengths: typeof existingSubmission.strengths === 'string'
        ? JSON.parse(existingSubmission.strengths)
        : existingSubmission.strengths ?? [],
      gapAreas: typeof existingSubmission.gapAreas === 'string'
        ? JSON.parse(existingSubmission.gapAreas)
        : existingSubmission.gapAreas ?? [],
    };
    return <ResultsScreen result={parsed} onContinue={() => navigate('/dashboard')} />;
  }

  if (loading) return (
    <div className="min-h-screen bg-cream-50 flex items-center justify-center">
      <div className="text-center space-y-3">
        <Loader2 size={32} className="animate-spin text-forest-600 mx-auto" />
        <p className="text-forest-500 text-sm">Loading assessment...</p>
      </div>
    </div>
  );

  if (error || areas.length === 0) return (
    <div className="min-h-screen bg-cream-50 flex items-center justify-center px-4">
      <div className="text-center space-y-3 max-w-sm">
        <AlertCircle size={36} className="mx-auto text-rust-400" />
        <h3 className="font-serif text-xl text-forest-900">Assessment not available</h3>
        <p className="text-sm text-forest-500">No questions have been configured yet. Please contact the administrator.</p>
        <button onClick={() => navigate('/dashboard')} className="btn-primary inline-flex">Skip for now →</button>
      </div>
    </div>
  );

  if (!question) return null;

  // Show start prompt before questions
  if (!started) {
    const totalQuestions = areas.reduce((s, a) => s + (a.questions?.length ?? 0), 0);
    return (
      <div className="min-h-screen bg-cream-50">
        {revisionNotice && (
          <div className="max-w-lg mx-auto pt-8 px-4">
            <div className="bg-rust-50 border border-rust-200 rounded-2xl p-4 flex items-start gap-3">
              <AlertCircle size={18} className="text-rust-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-rust-700">Your previous submission needs revision</p>
                <p className="text-sm text-rust-600 mt-1">{revisionNotice}</p>
              </div>
            </div>
          </div>
        )}
        <StartPromptScreen
          totalQuestions={totalQuestions}
          totalAreas={areas.length}
          onStart={() => setStarted(true)}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream-50">
      <Navbar />

      {/* Progress bar */}
      <div className="fixed top-0 left-0 right-0 z-40 h-1 bg-cream-200">
        <div className="h-full bg-forest-600 transition-all duration-500" style={{ width: `${progressPct}%` }} />
      </div>

      <div className="max-w-2xl mx-auto px-4 pt-24 pb-12">

        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-forest-500 mb-3">
            Area {currentArea + 1} of {areas.length}
          </div>
          <h1 className="font-serif text-2xl font-bold text-forest-900">{area.name}</h1>
          <p className="text-sm text-forest-500 mt-2 max-w-md mx-auto">{area.description}</p>
        </div>

        {/* Area progress dots */}
        <div className="flex justify-center gap-2 mb-8">
          {areas.map((a, i) => {
            const aDone = (a.questions || []).every(q => answers[q.id] !== undefined);
            return (
              <div key={i} className={`h-1.5 rounded-full transition-all duration-300 ${
                i < currentArea || aDone ? 'bg-forest-600 w-6'
                : i === currentArea ? 'bg-forest-400 w-8'
                : 'bg-cream-300 w-4'
              }`} />
            );
          })}
        </div>

        {/* Question card */}
        <div className="bg-white border border-cream-200 rounded-2xl p-6 shadow-sm space-y-6">
          <div>
            <div className="flex items-center justify-between mb-4">
              <span className="text-xs font-semibold text-forest-400 uppercase tracking-widest">
                Question {currentQ + 1} of {questions.length}
              </span>
              <span className="text-xs text-forest-400">{areaAnswered}/{questions.length} answered</span>
            </div>
            <p className="font-sans font-semibold text-forest-900 text-base leading-relaxed">
              {question.text}
            </p>
          </div>

          <QuestionCard question={question} answer={answer} onChange={handleAnswer} />

          {/* Navigation */}
          <div className="flex items-center justify-between pt-2">
            <button
              onClick={handlePrev}
              disabled={currentArea === 0 && currentQ === 0}
              className="flex items-center gap-2 text-sm text-forest-500 hover:text-forest-800 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
              <ChevronLeft size={16} /> Previous
            </button>

            <div className="text-xs text-forest-400">
              {answeredCount}/{totalQuestions} total
            </div>

            <button
              onClick={handleNext}
              disabled={submitting}
              className="btn-primary flex items-center gap-2 disabled:opacity-60">
              {submitting
                ? <><Loader2 size={14} className="animate-spin" /> Submitting...</>
                : isLastArea && isLastQ
                  ? 'Submit Assessment →'
                  : <>Next <ChevronRight size={14} /></>}
            </button>
          </div>
        </div>

        {/* Skip option */}
        <div className="text-center mt-6">
          <button onClick={() => navigate('/dashboard')}
            className="text-xs text-forest-400 hover:text-forest-600 transition-colors">
            Skip for now (you can complete this later)
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssessmentPage;